/**
 * Live task status + output panel. Polls every 2s until the task is terminal,
 * then renders its output. Extracted from SandboxPage so the sandbox can stack
 * one panel per task when a batch of JSON inputs is fanned out.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { getTask, getTaskOutput, streamTask } from '@optum/api-client/endpoints/tasks';
import type { TaskPayload } from '@optum/api-client/types';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';

import { Tabs } from '../../ui/Tabs.js';
import { Badge, Icon, Typography } from '../../ui/finixt.js';
import { CostPanel, useTaskCost } from '../cost/CostPanel.js';
import { LogPanel } from '../tasks/LogPanel.js';

import { ProfilePanel } from './ProfilePanel.js';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);
// A streaming task publishes its result while it runs, so the output is
// worth reading before the task is over.
const HAS_OUTPUT = new Set(['completed', 'failed', 'streaming']);

export function TaskResult({ taskId, title }: { taskId: string; title?: string }) {
  const task = useQuery({
    queryKey: qk.task(taskId),
    queryFn: () => getTask(taskId),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status && TERMINAL_STATUSES.has(status) ? false : 2000;
    },
  });

  const status = task.data?.status;
  const isStreaming = status === 'streaming';
  const isTerminal = Boolean(status && TERMINAL_STATUSES.has(status));
  const showOutput = Boolean(status && HAS_OUTPUT.has(status));
  // The chip appears at submission; the tab waits for the data, which the
  // terminal envelope carries. A run still in flight has neither yet.
  const hasProfile = Boolean(task.data?.profile_requested) && isTerminal;
  // The Cost tab appears once the run is over and recorded usage; the
  // route answers 404 for a run that spent nothing, which hides it.
  const cost = useTaskCost(taskId, isTerminal);
  const hasCost = Boolean(cost.data);
  // The tab row exists from the moment the run starts, so the log can be
  // watched before there is any output to show.
  const hasStarted = Boolean(status && status !== 'pending');
  const [tab, setTab] = useState<'output' | 'logs' | 'profile' | 'cost'>('output');

  // The live channel, for the low-latency half of the story. The poll above
  // stays the source of truth for status, so the panel still works wherever
  // an event stream is buffered or blocked.
  const [live, setLive] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    if (!isStreaming) return;
    const controller = new AbortController();
    streamTask(taskId, (frame) => setLive(frame.result), {
      signal: controller.signal,
    }).catch(() => {
      // Aborted on unmount, or the stream is unavailable here. Polling
      // continues either way; nothing to report.
    });
    return () => controller.abort();
  }, [taskId, isStreaming]);
  const output = useQuery({
    queryKey: qk.taskOutput(taskId),
    queryFn: () => getTaskOutput(taskId),
    enabled: Boolean(status && HAS_OUTPUT.has(status)),
    // Poll alongside the task while the result is still filling in. The
    // events endpoint is lower latency, but polling survives a proxy that
    // buffers an event stream, and this panel must work either way.
    refetchInterval: isStreaming ? 1000 : false,
  });

  if (task.isPending) return <LoadingBlock />;
  if (task.isError) {
    return (
      <ErrorBlock message={task.error instanceof Error ? task.error.message : 'Failed to load'} />
    );
  }
  const t = task.data;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 11, fontSize: 'var(--text-body-md)' }}
    >
      {title && (
        <Typography variant="card" as="h4">
          {title}
        </Typography>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <StatusBadge status={t.status} />
        {/* Marks the run from submission, before there is any profile to
            read, so a batch of panels can be scanned without opening them. */}
        {t.profile_requested && <Badge tone="neutral">profiled</Badge>}
        <code
          style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
          className="mono"
        >
          {t.id}
        </code>
      </div>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0,1fr)',
          columnGap: 16,
          rowGap: 5,
          fontSize: 'var(--text-meta-lg)',
        }}
      >
        <dt style={{ color: 'var(--text-muted)' }}>Created</dt>
        <dd>{absoluteDateTime(t.created_at)}</dd>
        <dt style={{ color: 'var(--text-muted)' }}>Started</dt>
        <dd>{t.started_at ? absoluteDateTime(t.started_at) : '—'}</dd>
        <dt style={{ color: 'var(--text-muted)' }}>Completed</dt>
        <dd>{t.completed_at ? absoluteDateTime(t.completed_at) : '—'}</dd>
        {t.failure_reason && (
          <>
            <dt style={{ color: 'var(--danger)' }}>Failure</dt>
            <dd style={{ wordBreak: 'break-all', color: 'var(--danger)' }}>{t.failure_reason}</dd>
          </>
        )}
      </dl>

      {!isTerminal && (
        <div
          style={{
            borderRadius: 'var(--radius-chip)',
            background: 'var(--surface-fill)',
            padding: 7,
            fontSize: 'var(--text-meta-lg)',
            color: 'var(--text-muted)',
          }}
        >
          {isStreaming
            ? `Answer arriving… status: ${t.status}`
            : `Polling every 2s… status: ${t.status}`}
        </div>
      )}

      {hasStarted && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Tabs
            items={[
              { value: 'output', label: 'Output', icon: 'output' },
              { value: 'logs' as const, label: 'Logs', icon: 'receipt_long' },
              ...(hasProfile
                ? [{ value: 'profile' as const, label: 'Profile', icon: 'speed' }]
                : []),
              ...(hasCost ? [{ value: 'cost' as const, label: 'Cost', icon: 'payments' }] : []),
            ]}
            value={tab}
            onChange={setTab}
          />

          {tab === 'logs' ? (
            <LogPanel taskId={taskId} status={t.status} />
          ) : tab === 'cost' && hasCost ? (
            <CostPanel taskId={taskId} />
          ) : tab === 'profile' && hasProfile ? (
            <ProfilePanel taskId={taskId} />
          ) : !showOutput ? (
            <EmptyBlock>No output yet.</EmptyBlock>
          ) : (
            <>
              {output.isPending && <LoadingBlock />}
              {output.isError && (
                <ErrorBlock
                  message={
                    output.error instanceof Error ? output.error.message : 'Failed to load output'
                  }
                />
              )}
              {live && <InlineJson value={live} />}
              {!live && output.data && <OutputBlock payload={output.data} />}
            </>
          )}

          {isTerminal && !t.profile_requested && (
            <p
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontSize: 'var(--text-meta-lg)',
                color: 'var(--text-muted)',
              }}
            >
              <Icon name="speed" size={14} />
              Profiler was off for this run.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function InlineJson({ value }: { value: Record<string, unknown> }) {
  return (
    <pre
      style={{
        overflow: 'auto',
        borderRadius: 'var(--radius-chip)',
        background: 'var(--surface-fill)',
        padding: 11,
        fontSize: 'var(--text-meta-lg)',
      }}
      className="mono"
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function OutputBlock({ payload }: { payload: TaskPayload }) {
  if (payload.inline) {
    return <InlineJson value={payload.inline} />;
  }
  if (payload.url) {
    return (
      <a
        href={payload.url}
        target="_blank"
        rel="noreferrer"
        style={{ color: 'var(--text-accent)', textDecoration: 'underline' }}
      >
        Download output ({payload.size_bytes.toLocaleString()} bytes)
      </a>
    );
  }
  return <EmptyBlock>Empty output.</EmptyBlock>;
}
